import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';

import './VideoZoomPopup.css';

//Enlarged single-camera view: shows one camera's frame, paused, at the group's current
//time. When a frameSource ({recording, camera}) is given, the frame is a full-resolution
//still extracted server-side from the camera's merged transport stream (camera_frame
//servlet; the browser cannot decode mpeg-ts, and the playable mp4s are 1024x768
//downscales) -- falling back to the paused mp4 video when that service or the ts is
//unavailable. Clicking off the window closes it (regular backdrop); resuming playback
//closes it from the outside via hide(). While it is open the media bar can keep seeking
//the group -- seek() moves this frame too and the popup stays put. No player controls:
//playback always belongs to the media bar.
class VideoZoomPopup extends Component {

    constructor(props) {
        super(props);

        this.state = {
            showPopup: false,
            src: '',
            time: 0,
            frameSource: null,      //{recording, camera} for the high-res ts frame service
            useVideoFallback: false,
            loading: true //blank until the requested frame lands
        };

        this.videoRef = React.createRef();
        this.imageRef = React.createRef();
    }

    show = (src, time, frameSource) => {

        console.log("ZoomFrame: opening " + (frameSource
            ? "full-res ts frame (camera " + frameSource.camera + " of '" + frameSource.recording + "' at " + time + "s)"
            : "mp4 video '" + src + "'"));

        this.setState({
            showPopup: true,
            src: src,
            time: time,
            frameSource: frameSource || null,
            useVideoFallback: !frameSource,
            loading: true
        });
    }

    frameUrl = () => {
        return 'camera_frame?recording=' + encodeURIComponent(this.state.frameSource.recording)
             + '&camera=' + encodeURIComponent(this.state.frameSource.camera)
             + '&time=' + encodeURIComponent(this.state.time);
    }

    handleFrameLoaded = () => {

        //the definitive answer to 'is this the high-res frame?': the decoded dimensions
        const image = this.imageRef.current;
        if (image) {
            console.log("ZoomFrame: ts frame loaded at " + image.naturalWidth + "x" + image.naturalHeight);
        }

        this.setState({ loading: false });
    }

    //no merged ts / servlet on this server: degrade to the paused mp4 video
    handleFrameError = () => {
        console.log("ZoomFrame: camera_frame failed (no merged cameraN.ts for this recording,"
                    + " or the service is not deployed) - falling back to the 1024x768 mp4");
        this.setState({ useVideoFallback: true, loading: true });
    }

    hide = () => {
        this.setState({
            showPopup: false,
            src: ''
        });
    }

    isOpen = () => {
        return this.state.showPopup;
    }

    //follow a media bar seek while open; also remembered so a (re)load lands on it.
    //In frame mode the src recomputes from the new time, fetching the frame there.
    seek = (time) => {

        this.setState({ time: time, loading: true });

        if (this.videoRef.current)
        {
            this.videoRef.current.currentTime = time;
        }
    }

    //the element ignores currentTime set before its metadata is in, so apply it (again) here.
    //Opening always seeks (even to the same position), so 'seeked' below always follows.
    handleLoadedMetadata = () => {

        if (this.videoRef.current)
        {
            this.videoRef.current.currentTime = this.state.time;
        }
    }

    //the frame is actually on screen now
    handleSeeked = () => {
        this.setState({ loading: false });
    }

    //draw the currently displayed frame to a canvas and download it as a png named after
    //the camera file and timestamp. In frame mode this captures the full-resolution ts
    //still; in fallback mode, the mp4 video frame.
    handleSaveFrame = () => {

        let source, width, height, time;
        const image = this.imageRef.current;
        const video = this.videoRef.current;

        if (image && image.complete && image.naturalWidth > 0)
        {
            source = image;
            width = image.naturalWidth;
            height = image.naturalHeight;
            time = this.state.time;
        }
        else if (video && video.videoWidth > 0)
        {
            source = video;
            width = video.videoWidth;
            height = video.videoHeight;
            time = video.currentTime;
        }
        else
        {
            return; //no frame available yet
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');

        if (!ctx)
        {
            return;
        }

        ctx.drawImage(source, 0, 0, width, height);

        const base = this.state.src.substring(this.state.src.lastIndexOf('/') + 1).replace(/\.[^.]+$/, '');
        const filename = base + '_' + time.toFixed(1) + 's.png';

        canvas.toBlob((blob) => {

            if (!blob)
            {
                return;
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 'image/png');
    }

    render() {
        return (
            <Modal
              className="videoZoomRoot"
              backdropClassName="videoZoomBackdrop"
              dialogClassName="videoZoomModal"
              show={this.state.showPopup}
              onHide={this.hide}
              animation={false}
              centered
            >
            <Modal.Body className="videoZoomBody">
                {this.state.frameSource && !this.state.useVideoFallback ? (
                    <img
                        ref={this.imageRef}
                        className="videoZoomImage"
                        src={this.frameUrl()}
                        alt=""
                        onLoad={this.handleFrameLoaded}
                        onError={this.handleFrameError}
                    />
                ) : (
                    <video
                        ref={this.videoRef}
                        className="videoZoomVideo"
                        src={this.state.src}
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={this.handleLoadedMetadata}
                        onSeeked={this.handleSeeked}
                    />
                )}
                {this.state.loading &&
                    <div className="videoZoomLoading">
                        <div className="videoZoomSpinner"></div>
                    </div>
                }
                <button type="button" className="videoZoomSave" aria-label="Save frame as png" title="Save frame as png" onClick={this.handleSaveFrame}>
                    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M8 2 v7 M4.8 6 L8 9.2 L11.2 6" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.8 11.5 v1.7 a0.8 0.8 0 0 0 0.8 0.8 h8.8 a0.8 0.8 0 0 0 0.8 -0.8 v-1.7" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                </button>
            </Modal.Body>
            </Modal>
        )
    }
}

export default VideoZoomPopup;
